import * as React from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface PublishSuccessDialogProps {
	isOpen: boolean;
	onClose: () => void;
	postUrl: string;
	templateName?: string;
	machineId: string;
	buzzUrl?: string;
	hideHistoryButton?: boolean;
}

const truncateUrl = (url: string) => {
	if (url.length > 80) {
		return url.substring(0, 77) + '...';
	}
	return url;
};

export function PublishSuccessDialog({
	isOpen,
	onClose,
	postUrl,
	templateName,
	machineId,
	buzzUrl,
	hideHistoryButton = false,
}: PublishSuccessDialogProps) {
	const navigate = useNavigate();

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className='sm:max-w-2xl'>
				<DialogTitle className='sr-only'>
					Post Published Successfully
				</DialogTitle>
				<div className='flex flex-col items-center justify-center py-8 space-y-4'>
					<img
						src='/logo.png'
						alt='Prometheus'
						className='h-12 mb-4'
					/>
					<motion.div
						initial={{ opacity: 0, scale: 0.5 }}
						animate={{ opacity: 1, scale: 1 }}
						className='text-3xl'
					>
						🎉
					</motion.div>
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						className='text-xl font-semibold text-purple-500 text-center'
					>
						Post Published Successfully!
					</motion.div>
					<div className='text-sm text-gray-400 text-center'>
						✨ Your content is now live and ready to engage ✨
					</div>

					<div className='space-y-4 mt-4'>
						<div
							className='p-4 bg-gradient-to-b from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-900 rounded-lg'
							style={{ maxWidth: '32rem' }}
						>
							<div className='space-y-3'>
								<div>
									<span className='text-sm font-semibold text-gray-500 dark:text-gray-400'>
										Published URL:
									</span>
									<a
										href={postUrl}
										target='_blank'
										rel='noopener noreferrer'
										className='block mt-1 text-blue-500 hover:text-blue-400 truncate'
										title={postUrl}
									>
										{truncateUrl(postUrl)}
									</a>
								</div>
								{buzzUrl && (
									<div>
										<span className='text-sm font-semibold text-gray-500 dark:text-gray-400'>
											Buzz Source:
										</span>
										<a
											href={buzzUrl}
											target='_blank'
											rel='noopener noreferrer'
											className='block mt-1 text-blue-500 hover:text-blue-400 truncate'
											title={buzzUrl}
										>
											{truncateUrl(buzzUrl)}
										</a>
									</div>
								)}
								<div>
									<span className='text-sm font-semibold text-gray-500 dark:text-gray-400'>
										Template Used:
									</span>
									<div className='mt-1 text-gray-600 dark:text-gray-300'>
										{templateName || 'No Template'}
									</div>
								</div>
							</div>
						</div>

						<div className='flex gap-3 pt-4'>
							<Button
								variant='outline'
								onClick={onClose}
								className={cn(
									'flex-1 bg-gradient-to-b from-gray-400 to-gray-600 hover:from-gray-500 hover:to-gray-700 text-white',
									hideHistoryButton && 'w-full'
								)}
							>
								Close
							</Button>
							{!hideHistoryButton && (
								<Button
									onClick={() =>
										navigate(
											`/admin/content-machine/${machineId}/history`
										)
									}
									className='flex-1 bg-gradient-to-b from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white'
								>
									View History
								</Button>
							)}
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
